import { CSSProperties, useMemo } from 'react';
import { Color, Size } from '@vivli/shared/theme';
import { LoadIndicatorView } from '../load-indicator/load-indicator.view';
import { LoadIndicatorColorEnum } from '@vivli/shared/infrastructure/enum';
import { AssetsConstant } from '@vivli/shared/infrastructure/constants';
import { IconDefinition } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';

export interface ButtonProps {
    disabled?: boolean;
    isLoading?: boolean;
    title?: string;
    dataId?: string;
    wrapText?: boolean;
    style?: CSSProperties;
    children?: any;
    onClick?: (e) => any;
    className?: string;
    icon?: 'red-x';
    faIcon?: IconDefinition;
    theme?:
        | 'white-on-blue'
        | 'blue-on-white'
        | 'green-on-white'
        | 'white-on-red'
        | 'red-on-white'
        | 'darkgreen-on-white'
        | 'white-on-lightblue'
        | 'darkblue-on-white';
}

const defaultButtonStyle = {
    height: 40,
    outline: 'none',
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    fontSize: Size.FontSize.Large,
    color: Color.WHITE,
    minWidth: 100,
    boxShadow: Size.BOX_SHADOW_LIGHT,
    backgroundColor: Color.TEXT_DARK_BLUE,
    borderTopRightRadius: Size.BORDER_RADIUS,
    borderBottomRightRadius: Size.BORDER_RADIUS,
    borderTopLeftRadius: Size.BORDER_RADIUS,
    borderBottomLeftRadius: Size.BORDER_RADIUS,
    paddingLeft: Size.PADDING,
    paddingRight: Size.PADDING,
    ':active': { transform: 'scale(0.98)' },
    ':hover': { opacity: Size.HOVER_OPACITY },
    transition: `scale 0.35s, opacity ${Size.HOVER_TRANSITION_TIME}s`,
    width: 'initial',
};

const loadIndicatorStyle: CSSProperties = {
    position: 'absolute',
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
};

export const ButtonView = ({
    style,
    disabled,
    isLoading,
    children,
    title,
    dataId,
    wrapText,
    theme = 'white-on-lightblue',
    faIcon,
    icon,
    ...optionalProps
}: ButtonProps) => {
    const buttonImageStyle = useMemo(() => {
        const baseStyle = {
            backgroundPosition: 'center right 11px',
            backgroundRepeat: 'no-repeat',
        };

        switch (icon) {
            case 'red-x':
                return {
                    ...baseStyle,
                    backgroundImage: `url(${AssetsConstant.RED_X_ICON})`,
                };
            default:
                return {};
        }
    }, [icon]);

    const buttonStyle = useMemo<CSSProperties>(() => {
        let fontColor = Color.WHITE;
        let backgroundColor = Color.VIVLI_LIGHT_BLUE;

        switch (theme) {
            case 'white-on-lightblue':
                backgroundColor = Color.VIVLI_LIGHT_BLUE;
                break;
            case 'white-on-blue':
                backgroundColor = Color.VIVLI_BLUE;
                break;
            case 'red-on-white':
                fontColor = Color.ERROR_RED;
                backgroundColor = Color.WHITE;
                break;
            case 'blue-on-white':
                fontColor = Color.TEXT_DARK_BLUE;
                backgroundColor = Color.WHITE;
                break;
            case 'darkblue-on-white':
                fontColor = Color.VIVLI_DARK_BLUE;
                backgroundColor = Color.WHITE;
                break;
            case 'green-on-white':
                fontColor = Color.VIVLI_GREEN;
                backgroundColor = Color.WHITE;
                break;
            case 'darkgreen-on-white':
                fontColor = Color.VIVLI_DARKER_GREEN;
                backgroundColor = Color.WHITE;
                break;
        }

        return {
            ...defaultButtonStyle,
            cursor: disabled || isLoading ? 'default' : 'pointer',
            pointerEvents: disabled || isLoading ? 'none' : null,
            opacity: disabled || isLoading ? Size.HOVER_OPACITY : 1,
            backgroundColor: backgroundColor,
            color: fontColor,
            border: backgroundColor === Color.WHITE ? `1px solid ${fontColor}` : 'none',
            ...buttonImageStyle,
            ...style,
        } as CSSProperties;
    }, [style, disabled, isLoading, theme, buttonImageStyle]);

    const loaderColor = useMemo(() => {
        switch (theme) {
            case 'red-on-white':
                return LoadIndicatorColorEnum.ErrorRed;
            case 'darkgreen-on-white':
                return LoadIndicatorColorEnum.DarkerGreen;
            case 'blue-on-white':
                return LoadIndicatorColorEnum.Blue;
            case 'white-on-red':
            case 'white-on-blue':
            case 'white-on-lightblue':
            default:
                return LoadIndicatorColorEnum.White;
        }
    }, [theme]);

    const wrapStyle = wrapText ? 'normal' : 'nowrap';

    return (
        <div title={title ? title : ''} style={buttonStyle} data-test-id={dataId} {...optionalProps} onClick={optionalProps.onClick}>
            {isLoading && <LoadIndicatorView color={loaderColor} style={loadIndicatorStyle} />}
            <span style={{ visibility: isLoading ? 'hidden' : 'visible', whiteSpace: wrapStyle }}>
                {faIcon && <FontAwesomeIcon style={{ color: buttonStyle.color, cursor: 'pointer' }} icon={faIcon} />}
                {children}
            </span>
        </div>
    );
};
